import { useMemo, useState } from 'react';
import { BarChart } from '../../charts/BarChart';
import { BarList, Card, ChartCard, DataTable, Drawer, DrawerSection, Segmented, Stat } from '../../components/data';
import { ErrorBanner, Field, Loading } from '../../components/ui';
import { num, pct, toLocalDateString, todayLocal } from '../../lib/format';
import { useHeldResource } from '../../lib/hooks';
import type { NocWorkload, TopAlert, TopAlertTicket, WorkloadSlice, WorkloadTally } from './nocTypes';

/**
 * The alert workload — what the NOC alert tickets cost, live from Autotask for
 * the days chosen (api/src/lib/noc/liveWorkload.ts), counted the way the
 * spreadsheet import (api/src/db/build-noc-workload.py) counted them.
 *
 * The question it answers is the one the library cannot: not "what does this
 * alert mean" but "how much of our time do alerts take, and where does it go".
 * Volume and hours by month, the clients and alert types that drive them, who
 * works them, how much is never touched by a person, and the Backups and
 * SentinelOne slices.
 *
 * Read once per range and kept until Refresh: one read is a few hundred
 * Autotask calls and can take a minute.
 */

/* At most 90 days: a longer read is thousands of tickets and a long wait.
   The API refuses more than that too. */
const MAX_DAYS = 90;
/** The first day of the last `days` days, today included. */
function daysBack(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - (days - 1));
  return toLocalDateString(d);
}
const spanDays = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;

const share = (part: number, whole: number) => (whole ? pct((part / whole) * 100, 1) : '—');
const hrs = (h: number) => `${num(Math.round(h))} h`;

/** "2026-03" → "Mar 26", the month axis label. */
const monthLabel = (m: string) => {
  const [y, mo] = m.split('-');
  return `${new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en-US', { month: 'short' })} ${y?.slice(2)}`;
};

export function NocWorkload({ onOpenAlert }: { onOpenAlert: (id: string) => void }) {
  const [typeView, setTypeView] = useState<'all' | 'undocumented' | 'documented'>('all');
  /* The range shown, and the one being typed: a date box changes only the
     draft, and Show applies it, so picking a date does not start a read of
     Autotask on every change. The quick buttons apply at once. */
  const [{ from, to }, setRange] = useState(() => ({ from: daysBack(30), to: todayLocal() }));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const quick = (days: number) => {
    const next = { from: daysBack(days), to: todayLocal() };
    setDraftFrom(next.from);
    setDraftTo(next.to);
    setRange(next);
  };
  const draftOk = Boolean(draftFrom && draftTo && draftFrom <= draftTo && spanDays(draftFrom, draftTo) <= MAX_DAYS);
  const tooLong = Boolean(from && to && spanDays(from, to) > MAX_DAYS);
  const valid = Boolean(from && to && from <= to) && !tooLong;
  const live = useHeldResource<NocWorkload>(valid ? `/noc/workload/live?${new URLSearchParams({ from, to })}` : null);
  const data = live.data;

  const controls = (
    <section className="card mb-4">
      <div className="card-body">
        <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-4)' }}>
          <Field label="Alert tickets created from">
            <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <input className="input" type="date" value={draftTo} min={draftFrom} max={todayLocal()} onChange={(e) => setDraftTo(e.target.value)} />
          </Field>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!draftOk || (draftFrom === from && draftTo === to)}
            onClick={() => setRange({ from: draftFrom, to: draftTo })}
            title={draftOk ? undefined : `Up to ${MAX_DAYS} days, start before end`}
          >
            Show
          </button>
          <div className="row" style={{ gap: 'var(--space-1)' }}>
            {[7, 30, 90].map((n) => (
              <button key={n} type="button" className="btn btn-ghost btn-sm" onClick={() => quick(n)}>
                Last {n} days
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" disabled={!valid || live.loading || live.refreshing} onClick={live.refresh}>
            {live.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
          </button>
        </div>
        <p className="xs muted mt-2">
          Live from Autotask, up to {MAX_DAYS} days at a time: tickets with issue type Automated Alert/Partner
          Tickets{data?.patchingQueues?.length ? `, and every ticket in ${data.patchingQueues.join(' and ')}` : ', and the Patching queue'},
          counted the way the spreadsheet was. After hours and weekends are in Denver time.
          {!draftOk && draftFrom && draftTo && ` Choose ${MAX_DAYS} days or fewer, the start before the end.`}
          {live.fetchedAt &&
            ` As of ${new Date(live.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
        </p>
      </div>
    </section>
  );

  if (!valid) {
    return (
      <>
        {controls}
        <ErrorBanner message={tooLong ? `Choose ${MAX_DAYS} days or fewer.` : 'The start date is after the end date.'} />
      </>
    );
  }
  if (live.loading && !data) return <>{controls}<Loading label="Reading the alert tickets from Autotask — a long range can take a minute or two" /></>;
  if (live.error) return <>{controls}<ErrorBanner message={live.error} /></>;
  if (!data) return controls;
  if (data.totals.tickets === 0) {
    return (
      <>
        {controls}
        <Card>
          <p className="sub">No alert tickets were created in this period.</p>
        </Card>
      </>
    );
  }

  const t = data.totals;
  const types =
    typeView === 'all'
      ? data.alertTypes
      : data.alertTypes.filter((a) => (typeView === 'documented' ? a.libraryId : !a.libraryId));

  return (
    <div className="noc-page">
      {controls}
      <p className="sub">
        {num(t.tickets)} alert tickets created {data.period.from} to {data.period.to}, read live from Autotask.
      </p>

      <div className="grid grid-4">
        <Stat label="Alert tickets" value={num(t.tickets)} foot={`${num(t.clients)} clients`} />
        <Stat label="Hours worked" value={hrs(t.hours)} foot={`${num(Math.round(t.billed))} h billed`} />
        <Stat
          label="Never touched by a person"
          value={share(t.zeroLabor, t.tickets)}
          foot={`${num(t.zeroLabor)} tickets with no labor`}
        />
        <Stat
          label="Unassigned"
          value={share(t.unassigned, t.tickets)}
          foot={`${num(t.unassigned)} with no primary resource`}
          flagged={t.unassigned / t.tickets > 0.5}
        />
        <Stat label="High or critical" value={num(t.highCritical)} foot={share(t.highCritical, t.tickets) + ' of tickets'} />
        <Stat label="After hours" value={share(t.afterHours, t.tickets)} foot={`${num(t.afterHours)} tickets`} />
        <Stat label="Weekend" value={share(t.weekend, t.tickets)} foot={`${num(t.weekend)} tickets`} />
        <Stat
          label="Documented in the library"
          value={share(data.coverage.documentedTickets, data.coverage.withAlertName)}
          foot={`${data.coverage.documentedTypes} of ${data.coverage.documentedTypes + data.coverage.undocumentedTypes} alert types`}
        />
      </div>

      <MonthlyCard title="Alert tickets by month" subtitle="Worked by someone, and never touched" months={data.monthly} />

      <div className="noc-split-5-7">
        <ChartCard
          title="Busiest clients"
          subtitle={`Top 15 of ${num(data.clients.length)} by alert tickets`}
          table={{
            columns: [
              { key: 'client', label: 'Client' },
              { key: 'tickets', label: 'Tickets', align: 'right', render: (r) => num(r.tickets) },
              { key: 'hours', label: 'Hours', align: 'right', render: (r) => num(r.hours) },
              { key: 'billed', label: 'Billed', align: 'right', render: (r) => num(r.billed) },
              { key: 'unassigned', label: 'Unassigned', align: 'right', render: (r) => share(r.unassigned, r.tickets) },
              { key: 'highCritical', label: 'High/critical', align: 'right', render: (r) => share(r.highCritical, r.tickets) },
            ],
            rows: data.clients,
            rowKey: (r) => r.client,
          }}
        >
          <BarList
            rows={data.clients.slice(0, 15).map((c) => ({
              name: c.client,
              sub: hrs(c.hours),
              value: c.tickets,
            }))}
          />
        </ChartCard>

        <ChartCard
          title="Labor by technician"
          subtitle="Hours worked on alert tickets, by primary resource"
          table={{
            columns: [
              { key: 'resource', label: 'Primary resource' },
              { key: 'tickets', label: 'Tickets', align: 'right', render: (r) => num(r.tickets) },
              { key: 'hours', label: 'Hours', align: 'right', render: (r) => num(r.hours) },
              { key: 'per', label: 'Hours / ticket', align: 'right', render: (r) => (r.tickets ? (r.hours / r.tickets).toFixed(2) : '—') },
              { key: 'billed', label: 'Billed', align: 'right', render: (r) => num(r.billed) },
            ],
            rows: data.resources,
            rowKey: (r) => r.resource,
          }}
        >
          <BarList
            rows={[...data.resources]
              .sort((a, b) => b.hours - a.hours)
              .slice(0, 12)
              .map((r) => ({ name: r.resource, sub: `${num(r.tickets)} tickets`, value: r.hours, display: hrs(r.hours) }))}
          />
        </ChartCard>
      </div>

      <div className="noc-split-5-7">
        <ChartCard
          title="Alert families"
          subtitle="Tickets by the RMM's alert family"
          table={{
            columns: [
              { key: 'family', label: 'Family' },
              { key: 'tickets', label: 'Tickets', align: 'right', render: (r) => num(r.tickets) },
              { key: 'hours', label: 'Hours', align: 'right', render: (r) => num(r.hours) },
            ],
            rows: data.families,
            rowKey: (r) => r.family,
          }}
        >
          <BarList rows={data.families.slice(0, 12).map((f) => ({ name: f.family, sub: hrs(f.hours), value: f.tickets }))} />
        </ChartCard>

        <Card
          title="Alert types"
          subtitle="Each alert the RMM raised, how often, and whether the library documents it"
          actions={
            <Segmented
              label="Which alert types"
              value={typeView}
              onChange={setTypeView}
              options={[
                { id: 'all', label: 'All' },
                { id: 'undocumented', label: `Undocumented (${data.coverage.undocumentedTypes})` },
                { id: 'documented', label: `In library (${data.coverage.documentedTypes})` },
              ]}
            />
          }
        >
          <DataTable
            rows={types}
            rowKey={(r) => r.name}
            columns={[
              { key: 'name', label: 'Alert', render: (r) => <span className="small">{r.name}</span> },
              { key: 'family', label: 'Family', render: (r) => <span className="xs muted">{r.family}</span> },
              { key: 'tickets', label: 'Tickets', align: 'right', render: (r) => num(r.tickets) },
              { key: 'hours', label: 'Hours', align: 'right', render: (r) => num(r.hours) },
              {
                key: 'library',
                label: 'Library',
                render: (r) =>
                  r.libraryId ? (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpenAlert(r.libraryId!)}>
                      {r.libraryId}
                    </button>
                  ) : (
                    <span className="badge badge-warning">Not documented</span>
                  ),
              },
            ]}
          />
        </Card>
      </div>

      {data.topAlerts && <TopAlerts top={data.topAlerts} />}

      <SliceSection title="Backups" note="Backup alert tickets — Datto and Intronis/Keep." slice={data.backups} />
      <SliceSection title="SentinelOne" note="SentinelOne and threat-response tickets." slice={data.sentinelOne} />
    </div>
  );
}

/**
 * The 20 alerts opened most in the period — by the RMM's alert name, or the
 * ticket title where there is none — across every queue, or within one.
 */
function TopAlerts({ top }: { top: NonNullable<NocWorkload['topAlerts']> }) {
  const [queue, setQueue] = useState('');
  /* The alert opened from the table, with its tickets. */
  const [drill, setDrill] = useState<TopAlert | null>(null);
  const canDrill = Boolean(top.tickets);
  const queues = Object.keys(top.byQueue).filter((q) => top.byQueue[q]!.length > 0);
  const rows = queue ? (top.byQueue[queue] ?? []) : top.all;
  return (
    <section className="card mb-4">
      <div className="card-head">
        <div className="grow">
          <h3>Top 20 alerts opened</h3>
          <p className="sub">
            {queue ? `In ${queue}.` : 'Across all queues.'} By the RMM&apos;s alert name, or the ticket title where there is none.
            {canDrill && ' Click an alert for its tickets.'}
          </p>
        </div>
        <select className="select" style={{ width: 'auto' }} value={queue} onChange={(e) => setQueue(e.target.value)} aria-label="Queue">
          <option value="">All queues</option>
          {queues.map((q) => (
            <option key={q} value={q}>
              {q}
            </option>
          ))}
        </select>
      </div>
      {rows.length === 0 ? (
        <div className="card-body">
          <p className="muted small">No alerts in this queue.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="num">#</th>
                <th>Alert</th>
                <th className="num">Tickets</th>
                <th className="num">Hours</th>
                <th className="num">Never touched</th>
                {!queue && <th>Queues</th>}
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, i) => (
                <tr key={a.name}>
                  <td className="num muted">{i + 1}</td>
                  <td>
                    {canDrill ? (
                      <button type="button" className="link-button" onClick={() => setDrill(a)}>
                        {a.name}
                      </button>
                    ) : (
                      a.name
                    )}
                  </td>
                  <td className="num strong">{num(a.tickets)}</td>
                  <td className="num">{num(Math.round(a.hours * 10) / 10)}</td>
                  <td className="num">{share(a.zeroLabor, a.tickets)}</td>
                  {!queue && (
                    <td className="xs">
                      {a.queues.map((q) => `${q.queue} ${num(q.count)}`).join(' · ')}
                    </td>
                  )}
                  <td className="xs">{a.lastSeen}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {drill && top.tickets && (
        <TopAlertDrawer
          alert={drill}
          queue={queue}
          tickets={(drill.ticketIdx ?? []).map((i) => top.tickets![i]!).filter(Boolean)}
          onClose={() => setDrill(null)}
        />
      )}
    </section>
  );
}

/**
 * One top alert, opened: what it cost, which clients and technicians it
 * lands on, and every ticket — each linked to Autotask.
 */
function TopAlertDrawer({
  alert,
  queue,
  tickets,
  onClose,
}: {
  alert: TopAlert;
  queue: string;
  tickets: TopAlertTicket[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter(
      (t) =>
        (!client || t.client === client) &&
        (!q || [t.number, t.title, t.client, t.resource, t.queue, t.priority].some((v) => v.toLowerCase().includes(q))),
    );
  }, [tickets, search, client]);
  const tallyOf = (key: (t: TopAlertTicket) => string) => {
    const m = new Map<string, { count: number; hours: number }>();
    for (const t of tickets) {
      const k = key(t) || '(none)';
      const e = m.get(k) ?? { count: 0, hours: 0 };
      e.count += 1;
      e.hours += t.hours;
      m.set(k, e);
    }
    return [...m].sort((a, b) => b[1].count - a[1].count);
  };
  const clients = tallyOf((t) => t.client);
  const people = tallyOf((t) => t.resource || 'Unassigned');
  const when = (iso: string) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const capped = tickets.length < alert.tickets;

  return (
    <Drawer open onClose={onClose} wide title={alert.name} subtitle={`${queue ? `In ${queue} · ` : ''}${num(alert.tickets)} tickets · last seen ${alert.lastSeen}`}>
      <div className="grid grid-4 mb-4">
        <Stat label="Tickets" value={num(alert.tickets)} foot={`${num(alert.highCritical)} high or critical`} />
        <Stat label="Hours worked" value={num(Math.round(alert.hours * 10) / 10)} foot={`${num(Math.round(alert.billed * 10) / 10)} billed`} />
        <Stat label="Never touched" value={share(alert.zeroLabor, alert.tickets)} foot="no time logged" />
        <Stat label="After hours" value={share(alert.afterHours, alert.tickets)} foot={`${share(alert.weekend, alert.tickets)} at weekends`} />
      </div>

      <div className="noc-drill-split mb-4">
        <DrawerSection title="By client" count={`${clients.length}`}>
          <table className="table">
            <tbody>
              {clients.slice(0, 10).map(([name, e]) => (
                <tr key={name} className={client === name ? 'is-changed' : undefined}>
                  <td>
                    <button type="button" className="link-button" onClick={() => setClient((c) => (c === name ? '' : name))}>
                      {name}
                    </button>
                  </td>
                  <td className="num strong">{num(e.count)}</td>
                  <td className="num xs muted">{num(Math.round(e.hours * 10) / 10)} h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DrawerSection>
        <DrawerSection title="By technician" count={`${people.length}`}>
          <table className="table">
            <tbody>
              {people.slice(0, 10).map(([name, e]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td className="num strong">{num(e.count)}</td>
                  <td className="num xs muted">{num(Math.round(e.hours * 10) / 10)} h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DrawerSection>
      </div>

      <DrawerSection title="Tickets" count={`${num(shown.length)}${capped ? ` of the newest ${num(tickets.length)}` : ''}`}>
        <div className="row wrap mb-2" style={{ gap: 'var(--space-2)' }}>
          <input className="input" style={{ maxWidth: 280 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticket, client, technician" aria-label="Search tickets" />
          {client && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setClient('')}>
              Client: {client} ✕
            </button>
          )}
        </div>
        {capped && <p className="xs muted mb-2">Showing the newest {num(tickets.length)} of {num(alert.tickets)} tickets; the figures above count them all.</p>}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Opened</th>
                <th>Ticket</th>
                <th>Client</th>
                <th>Queue</th>
                <th>Technician</th>
                <th>Priority</th>
                <th className="num">Hours</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t.id}>
                  <td className="xs" style={{ whiteSpace: 'nowrap' }}>{when(t.opened)}</td>
                  <td>
                    {t.link ? (
                      <a href={t.link} target="_blank" rel="noreferrer" className="mono xs">
                        {t.number} ↗
                      </a>
                    ) : (
                      <span className="mono xs">{t.number}</span>
                    )}
                    <div className="xs muted">{t.title}</div>
                  </td>
                  <td className="xs">{t.client || '—'}</td>
                  <td className="xs">{t.queue || '—'}</td>
                  <td className="xs">{t.resource || <span className="muted">Unassigned</span>}</td>
                  <td className="xs">{t.priority || '—'}</td>
                  <td className="num">{t.hours ? num(t.hours) : <span className="muted">0</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DrawerSection>
    </Drawer>
  );
}

/** Tickets per month, split by whether anyone worked them, with hours in the table. */
function MonthlyCard({
  title,
  subtitle,
  months,
}: {
  title: string;
  subtitle: string;
  months: (WorkloadTally & { month: string })[];
}) {
  const rows = months.map((m) => ({
    month: m.month,
    worked: m.tickets - m.zeroLabor,
    untouched: m.zeroLabor,
  }));
  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      table={{
        columns: [
          { key: 'month', label: 'Month', render: (r) => monthLabel(r.month) },
          { key: 'tickets', label: 'Tickets', align: 'right', render: (r) => num(r.tickets) },
          { key: 'hours', label: 'Hours', align: 'right', render: (r) => num(r.hours) },
          { key: 'zeroLabor', label: 'No labor', align: 'right', render: (r) => num(r.zeroLabor) },
          { key: 'unassigned', label: 'Unassigned', align: 'right', render: (r) => num(r.unassigned) },
          { key: 'highCritical', label: 'High/critical', align: 'right', render: (r) => num(r.highCritical) },
          { key: 'billed', label: 'Billed', align: 'right', render: (r) => num(r.billed) },
        ],
        rows: months,
        rowKey: (r) => r.month,
        scroll: false,
      }}
    >
      <BarChart
        data={rows}
        xKey="month"
        stacked
        series={[
          { key: 'worked', label: 'Worked by someone', color: 'var(--series-1)' },
          { key: 'untouched', label: 'No labor', color: 'var(--series-3)' },
        ]}
        xFormat={monthLabel}
        yFormat={(v) => num(v)}
        height={240}
      />
    </ChartCard>
  );
}

/** One slice of the tickets — Backups, SentinelOne — on its own. */
function SliceSection({ title, note, slice }: { title: string; note: string; slice: WorkloadSlice }) {
  const t = slice.totals;
  return (
    <>
      <h2 className="noc-slice-h">{title}</h2>
      <p className="sub">{note}</p>
      <div className="grid grid-4">
        <Stat label="Tickets" value={num(t.tickets)} />
        <Stat label="Hours worked" value={hrs(t.hours)} />
        <Stat label="High or critical" value={num(t.highCritical)} foot={share(t.highCritical, t.tickets)} />
        <Stat label="Unassigned" value={share(t.unassigned, t.tickets)} />
      </div>
      <div className="noc-split-5-7">
        <MonthlyCard title={`${title} by month`} subtitle="Worked by someone, and never touched" months={slice.monthly} />
        <ChartCard
          title={`${title} by client`}
          subtitle="Top 15 by tickets"
          table={{
            columns: [
              { key: 'client', label: 'Client' },
              { key: 'tickets', label: 'Tickets', align: 'right', render: (r) => num(r.tickets) },
              { key: 'hours', label: 'Hours', align: 'right', render: (r) => num(r.hours) },
            ],
            rows: slice.clients,
            rowKey: (r) => r.client,
          }}
        >
          <BarList rows={slice.clients.map((c) => ({ name: c.client, sub: hrs(c.hours), value: c.tickets }))} />
        </ChartCard>
      </div>
      {slice.kinds.length > 1 && (
        <p className="sub">
          By kind:{' '}
          {slice.kinds.map((k) => `${k.kind} ${num(k.tickets)}`).join(' · ')}
        </p>
      )}
    </>
  );
}
